import React,{ useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Alert, Badge, Button, ButtonGroup, CloseButton, Col, Container, FloatingLabel, Form, Image, InputGroup, Modal, ModalBody, ProgressBar, Row, Spinner, Tab, Tabs, Toast, ToggleButton } from 'react-bootstrap';
import { AtomicBlockUtils, EditorState, RichUtils, convertFromRaw, convertToRaw } from 'draft-js';
import 'draft-js/dist/Draft.css';
import Editor,{ createEditorStateWithText, composeDecorators } from '@draft-js-plugins/editor';
import createStaticToolbarPlugin from '@draft-js-plugins/static-toolbar';
import '@draft-js-plugins/static-toolbar/lib/plugin.css';
import createInlineToolbarPlugin, { Separator } from '@draft-js-plugins/inline-toolbar';
import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  HeadlineOneButton,
  HeadlineTwoButton,
  HeadlineThreeButton,
} from '@draft-js-plugins/buttons';
import createLinkPlugin from '@draft-js-plugins/anchor';
import '@draft-js-plugins/anchor/lib/plugin.css';
import createImagePlugin from '@draft-js-plugins/image';
import '@draft-js-plugins/image/lib/plugin.css';
import createVideoPlugin from '@draft-js-plugins/video';
import '@draft-js-plugins/video/lib/plugin.css';
import createTextAlignmentPlugin from '@draft-js-plugins/text-alignment';
import '@draft-js-plugins/text-alignment/lib/plugin.css';

import createAlignmentPlugin from '@draft-js-plugins/alignment';
import createFocusPlugin from '@draft-js-plugins/focus';
import createResizeablePlugin from '@draft-js-plugins/resizeable';
import '@draft-js-plugins/focus/lib/plugin.css';
import '@draft-js-plugins/alignment/lib/plugin.css';

import ApiFetch from '../ApiFetch';
import DynamicDialog from '../DynamicDialog';

const focusPlugin = createFocusPlugin();
const alignmentPlugin = createAlignmentPlugin();
const resizeablePlugin = createResizeablePlugin();
const { AlignmentTool } = alignmentPlugin;

const decorator = composeDecorators(
  resizeablePlugin.decorator,
  alignmentPlugin.decorator,
  focusPlugin.decorator
);

const staticToolbarPlugin = createStaticToolbarPlugin();
const { Toolbar } = staticToolbarPlugin;
const textAlignmentPlugin = createTextAlignmentPlugin();
const imagePlugin = createImagePlugin({ decorator });

function MyEditor(props) {
  const [plugins, InlineToolbar, LinkButton] = useMemo(() => {
    const linkPlugin = createLinkPlugin({ placeholder: 'https://...' });
    const videoPlugin = createVideoPlugin();
    const inlineToolbarPlugin = createInlineToolbarPlugin();

    return [
      [
        focusPlugin,
        alignmentPlugin,
        resizeablePlugin,
        imagePlugin,
        textAlignmentPlugin,
        staticToolbarPlugin,
        inlineToolbarPlugin,
        linkPlugin,
        videoPlugin
      ],
      inlineToolbarPlugin.InlineToolbar,
      linkPlugin.LinkButton,
    ];
  }, []);

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );

  const saveContent = () => {
    const contentState = editorState.getCurrentContent();
    const raw = convertToRaw(contentState);
    console.log(raw);
  };

  const toggleHeaderOne = (event) => {
    event.preventDefault();
    setEditorState(RichUtils.toggleBlockType(editorState, 'header-one'));
  };

  const [uploading, setUploading] = useState(false);
  const [progressNow, setProgressNow] = useState(0);
  const [progressLabel, setProgressLabel] = useState(0);

  const handleDroppedFiles = (selection, files) => {
    setUploading(true);
    let fd = new FormData();
    fd.append("file[]", files[0]);
    let req = new XMLHttpRequest();
    req.open("POST", ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/upload/imageFile");
    req.withCredentials = true;
    req.onreadystatechange = () => {
      if (req.readyState === 4 && req.status === 200) {
        if (req.responseText !== "") {
          var r = JSON.parse(req.responseText);
          if (r.result) {
            insertImage(ApiFetch.API_PROTOCOL + '//' + ApiFetch.IMG_HOST + r.data.url);
          }
          setUploading(false);
        }
      }
    };
    req.onerror = () => {
      setUploading(false);
      setProgressNow(0);
      setProgressLabel(0);
      alert("通信エラーが発生しました。");
    };
    req.upload.onprogress = (e) => {
      let per = Math.floor(e.loaded / e.total * 100);
      let label = "";
      if (per === 100) {
        label = "登録中";
      } else {
        label = per + "%";
      }
      setProgressNow(per);
      setProgressLabel(label);
    };
    req.send(fd);
  };

  const insertImage = (url) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'image',
      'IMMUTABLE',
      { 
        src: url,
        resizable: true
      }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    onChange(
      AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ')
    );
  };

  useEffect(() => {
    setEditorState(createEditorStateWithText("\n\n\n\n\n"));
  }, []);

  useEffect(() => {
    if (props.file !== undefined) handleDroppedFiles(null, [props.file]);
  }, [props.file]);

  useEffect(() => {
    if (props.raw === undefined) return;
    const contentState = convertFromRaw(props.raw);
    const editorState = EditorState.createWithContent(contentState);
    setEditorState(editorState);
  }, [props.raw]);

  const editorRef = useRef(null);
  
  const onChange = (value) => {
    if (props.onChange === undefined) return;
    setEditorState(value);
    props.onChange(value);
  };

  return (
    <div>
      <div className="editor" style={{ border: "1px solid #DDDDDD", borderRadius: "5px", position: 'relative' }}>
        {uploading ? (
          <DynamicDialog
            width={300}
            height={58}
            show={uploading}
            switch={setUploading}
            content={
              <ProgressBar
                id="file-insert-progress"
                animated
                now={progressNow}
                label={progressLabel}
                style={{ marginBottom: "10px" }}
              />
            }
          />
        ) : ""}
        <div className="editor-toolbar">
          <staticToolbarPlugin.Toolbar>
            {(externalProps) => (
              <div>
                <ItalicButton {...externalProps} />
                <BoldButton {...externalProps} />
                <UnderlineButton {...externalProps} />
                <textAlignmentPlugin.TextAlignment {...externalProps} />
                <Separator {...externalProps} />
                <HeadlineOneButton {...externalProps} />
                <HeadlineTwoButton {...externalProps} />
                <HeadlineThreeButton {...externalProps} />
              </div>
            )}
          </staticToolbarPlugin.Toolbar>
        </div>
        <div className="editor-content">
          <Editor
            editorState={editorState}
            onChange={onChange}
            plugins={plugins}
            handleDroppedFiles={handleDroppedFiles}
            placeholder='本文'
          />
          <AlignmentTool />
          <InlineToolbar>
            {(externalProps) => (
              <>
                <ItalicButton {...externalProps} />
                <BoldButton {...externalProps} />
                <UnderlineButton {...externalProps} />
                <textAlignmentPlugin.TextAlignment {...externalProps} />
                <Separator {...externalProps} />
                <HeadlineOneButton {...externalProps} />
                <HeadlineTwoButton {...externalProps} />
                <HeadlineThreeButton {...externalProps} />
                <LinkButton {...externalProps} />
              </>
            )}
          </InlineToolbar>
        </div>
      </div>
      <style jsx>{`
        .editor {
          background: #fff;
          min-height: 300px;
        }
        .editor-toolbar {
          border-bottom: 1px solid #ddd;
          padding: 5px;
        }
        .editor-content {
          padding: 10px;
          min-height: 200px;
        }
      `}</style>
    </div>
  );
}

export default MyEditor;